import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getRecruiterProfile,
  saveRecruiterProfile,
  sendRecruiterWebhookTest,
  type RecruiterProfileInput,
} from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { LoadingButton } from '@/components/spectrumui/loading-button'
import { Switch } from '@/components/motion/switch'
import { apiErrorMessage } from '@/api/client'
import { useAuth } from '@/contexts/AuthContext'
import { initials } from '@/lib/dashboard-utils'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import { usePageMeta } from '@/hooks/use-page-meta';

/** A 32-byte hex signing secret, generated in the browser so it never has to
 * be minted and echoed back by the server. */
function generateWebhookSecret(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

interface NotificationPref {
  stateKey: 'completed' | 'started' | 'expiring'
  title: string
  description: string
}

// One row per sender in utils/notifications.py. Order matches the email
// story: what happens after the interview, during it, and before the link dies.
const NOTIFICATION_PREFS: NotificationPref[] = [
  {
    stateKey: 'completed',
    title: 'Report ready',
    description: 'Email me when a candidate finishes and their report is in.',
  },
  {
    stateKey: 'started',
    title: 'Interview started',
    description: 'Email me the moment a candidate starts an interview. One email per redemption, so it is off by default.',
  },
  {
    stateKey: 'expiring',
    title: 'Link expiring soon',
    description: 'Email me when a pending invite link is about to expire (about a day ahead).',
  },
]

function NotificationRow({ pref, checked, disabled, onChange }: {
  pref: NotificationPref
  checked: boolean
  disabled: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-6" data-testid={`pref-${pref.stateKey}`}>
      <div>
        <p className="text-sm font-semibold">{pref.title}</p>
        <p className="hint mt-1">{pref.description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        label={`${pref.title} emails`}
        onCheckedChange={onChange}
      />
    </div>
  )
}

export function RecruiterProfile() {
  usePageMeta("Company profile — Aura Recruiter")
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user } = useAuth()

  const [companyName, setCompanyName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [companyWebsite, setCompanyWebsite] = useState('')
  const [companyLocation, setCompanyLocation] = useState('')
  const [notify, setNotify] = useState({ completed: true, started: false, expiring: true })
  const [bookingUrl, setBookingUrl] = useState('')
  const [webhookUrl, setWebhookUrl] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [webhookConfigured, setWebhookConfigured] = useState(false)
  const [webhookDisabled, setWebhookDisabled] = useState(false)
  const [testing, setTesting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  // Async continuations (load, save) must not touch state after unmount.
  const aliveRef = useRef(true)
  useEffect(() => () => { aliveRef.current = false }, [])

  useEffect(() => {
    let cancelled = false
    getRecruiterProfile()
      .then(p => {
        if (cancelled) return
        setCompanyName(p.company_name)
        setJobTitle(p.job_title)
        setCompanyWebsite(p.company_website ?? '')
        setCompanyLocation(p.company_location)
        setNotify({
          completed: p.notify_completed,
          started: p.notify_started,
          expiring: p.notify_expiring,
        })
        setBookingUrl(p.booking_url ?? '')
        setWebhookUrl(p.webhook_url ?? '')
        setWebhookConfigured(p.webhook_configured)
        setWebhookDisabled(p.webhook_disabled)
      })
      .catch(err => {
        if (cancelled) return
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/', { replace: true })
          return
        }
        setLoadError('Failed to load your profile.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [navigate, reloadKey])

  // A failed load must not leave a blank, submittable form: saving it would
  // overwrite the real profile (and its booking link) with empty defaults.
  const retryLoad = () => {
    setLoadError(null)
    setLoading(true)
    setReloadKey(k => k + 1)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setSaveError(null)
    try {
      const payload: RecruiterProfileInput = {
        company_name: companyName.trim(),
        job_title: jobTitle.trim(),
        company_website: companyWebsite.trim() || null,
        company_location: companyLocation.trim(),
        notify_completed: notify.completed,
        notify_started: notify.started,
        notify_expiring: notify.expiring,
        booking_url: bookingUrl.trim() || null,
        webhook_url: webhookUrl.trim() || null,
      }
      // Only send a secret when the recruiter typed one; blank means "keep
      // the stored secret". The server never echoes it back.
      const trimmedSecret = webhookSecret.trim()
      if (trimmedSecret) payload.webhook_secret = trimmedSecret
      const saved = await saveRecruiterProfile(payload)
      if (!aliveRef.current) return
      // The secret field is cleared after any save — it must never linger in
      // the form (and is never prefilled from the server).
      setWebhookSecret('')
      setWebhookUrl(saved.webhook_url ?? '')
      setWebhookConfigured(saved.webhook_configured)
      setWebhookDisabled(saved.webhook_disabled)
      toast({ title: 'Company profile saved', description: 'Candidates see your name and company on interview invites.' })
    } catch (error) {
      if (!aliveRef.current) return
      setSaveError(apiErrorMessage(error, 'Could not save your profile.'))
    } finally {
      if (aliveRef.current) setSaving(false)
    }
  }

  const sendTestEvent = async () => {
    setTesting(true)
    try {
      // Probe the form's values, not the saved ones — otherwise the toast can
      // report success for an unsaved URL against a stale saved endpoint.
      const result = await sendRecruiterWebhookTest(
        webhookUrl.trim() || null,
        webhookSecret.trim() || null,
      )
      if (!aliveRef.current) return
      toast({
        title: result.ok ? 'Test event sent' : 'Test event failed',
        description: result.detail,
        variant: result.ok ? undefined : 'destructive',
      })
    } catch (error) {
      if (!aliveRef.current) return
      toast({
        title: 'Test event failed',
        description: apiErrorMessage(error, 'Could not reach the webhook.'),
        variant: 'destructive',
      })
    } finally {
      if (aliveRef.current) setTesting(false)
    }
  }

  return (
    <AppShell active="profile">
      <Toaster />
      <div className="wnew-grid">
        <header className="screen-head">
          <div>
            <h1>Company profile</h1>
            <p>
              Your name and company show up on every invite you send, so candidates know
              who they're interviewing with before they start.
            </p>
          </div>
        </header>

        {loading ? (
          <div className="wloading" role="status" aria-live="polite">
            <span className="wspinner" aria-hidden="true"></span>
            <span>Loading profile…</span>
          </div>
        ) : loadError ? (
          <section className="panel">
            <div className="empty">
              <h3>{loadError}</h3>
              <p className="hint mt-2">
                Your saved profile is unchanged. Retry before editing so a save can't
                overwrite it with blank values.
              </p>
              <div className="empty-cta">
                <button type="button" className="wbtn wbtn-primary" onClick={retryLoad}>
                  Retry
                </button>
              </div>
            </div>
          </section>
        ) : (
          <>
            <form className="panel wform-panel" aria-label="Company profile" onSubmit={submit}>
              <div className="field">
                <label htmlFor="rp-company">Company name</label>
                <input
                  id="rp-company"
                  type="text"
                  placeholder="e.g. Acme Robotics"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  maxLength={200}
                />
              </div>

              <div className="field">
                <label htmlFor="rp-title">Your title</label>
                <input
                  id="rp-title"
                  type="text"
                  placeholder="e.g. Engineering Manager"
                  value={jobTitle}
                  onChange={e => setJobTitle(e.target.value)}
                  maxLength={120}
                />
              </div>

              <div className="field">
                <label htmlFor="rp-website">Company website</label>
                <input
                  id="rp-website"
                  type="url"
                  placeholder="https://acme.example"
                  value={companyWebsite}
                  onChange={e => setCompanyWebsite(e.target.value)}
                  maxLength={512}
                />
              </div>

              <div className="field">
                <label htmlFor="rp-location">Company location</label>
                <input
                  id="rp-location"
                  type="text"
                  placeholder="e.g. Remote / Berlin"
                  value={companyLocation}
                  onChange={e => setCompanyLocation(e.target.value)}
                  maxLength={120}
                />
              </div>

              <div className="field">
                <label htmlFor="rp-booking">Booking link</label>
                <input
                  id="rp-booking"
                  type="url"
                  placeholder="https://calendly.com/you/intro-call"
                  value={bookingUrl}
                  onChange={e => setBookingUrl(e.target.value)}
                  maxLength={512}
                />
                <span className="hint">
                  Shows as "Book a follow-up interview" on completed reports — your Calendly,
                  Cal.com, or any other https scheduling page.
                </span>
              </div>

              <section className="mt-2 border-t border-border pt-6" aria-label="Outbound webhook">
                <h2 className="text-sm font-bold">Outbound webhook</h2>
                <p className="hint mt-1">
                  POST a signed <code>interview.completed</code> event to your automation tool
                  (Zapier, Make, n8n) whenever a candidate finishes one of your interviews.
                </p>

                {webhookDisabled && (
                  <div className="wbanner is-flat mt-4" role="alert">
                    This webhook was disabled after repeated delivery failures. Change the URL
                    or enter a new signing secret, then save to re-enable it.
                  </div>
                )}

                <div className="mt-5 grid gap-5">
                  <div className="field">
                    <label htmlFor="rp-webhook-url">Webhook URL</label>
                    <input
                      id="rp-webhook-url"
                      type="url"
                      placeholder="https://hooks.zapier.com/hooks/catch/…"
                      value={webhookUrl}
                      onChange={e => setWebhookUrl(e.target.value)}
                      maxLength={512}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="rp-webhook-secret">Signing secret</label>
                    <div className="flex items-center gap-2">
                      <input
                        id="rp-webhook-secret"
                        type="password"
                        autoComplete="off"
                        placeholder={webhookConfigured ? 'Leave blank to keep the current secret' : 'At least 16 characters'}
                        value={webhookSecret}
                        onChange={e => setWebhookSecret(e.target.value)}
                        maxLength={128}
                      />
                      <button
                        type="button"
                        className="wbtn"
                        onClick={() => setWebhookSecret(generateWebhookSecret())}
                      >
                        Generate
                      </button>
                    </div>
                    <span className="hint">
                      {webhookConfigured
                        ? 'A secret is saved. Leave this blank to keep it, or enter a new one to replace it.'
                        : 'Used to sign each payload (HMAC-SHA256). The same secret must be configured on your receiver.'}
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    className="wbtn"
                    disabled={testing || saving || !webhookUrl.trim()}
                    onClick={sendTestEvent}
                  >
                    {testing ? 'Sending…' : 'Send test event'}
                  </button>
                </div>
              </section>

              <section className="mt-2 border-t border-border pt-6" aria-label="Email notifications">
                <h2 className="text-sm font-bold">Email notifications</h2>
                <p className="hint mt-1">
                  Delivered to your account email ({user?.email}). Toggles save with this form.
                </p>
                <div className="mt-5 grid gap-5">
                  {NOTIFICATION_PREFS.map(pref => (
                    <NotificationRow
                      key={pref.stateKey}
                      pref={pref}
                      checked={notify[pref.stateKey]}
                      disabled={saving}
                      onChange={next => setNotify(n => ({ ...n, [pref.stateKey]: next }))}
                    />
                  ))}
                </div>
              </section>

              {saveError && <div className="wbanner is-flat mx-5" role="alert">{saveError}</div>}

              <div className="form-actions wform-actions">
                <span aria-hidden="true" className="wspacer" />
                <LoadingButton type="submit" size="lg" loading={saving}>
                  Save profile
                </LoadingButton>
              </div>
            </form>

            <aside className="wnew-aside" aria-label="Account and invite preview">
              <section className="panel waside-card">
                <h2>Signed in as</h2>
                <div className="widentity-inner mt-3">
                  <span className="avatar" aria-hidden="true">{initials(user?.name ?? '')}</span>
                  <div>
                    <strong className="widentity-name">{user?.name ?? 'Recruiter'}</strong>
                    <span className="hint">{user?.email}</span>
                  </div>
                </div>
              </section>

              <section className="panel waside-card">
                <h2>Candidates see</h2>
                <div className="widentity-inner mt-3">
                  <span className="avatar" aria-hidden="true">{initials(user?.name ?? '')}</span>
                  <div>
                    <strong className="widentity-name">{user?.name ?? 'Recruiter'}</strong>
                    <span className="hint">{companyName.trim() || 'Your company shows up here'}</span>
                  </div>
                </div>
                <p className="hint mt-3">
                  This is how every invite introduces you — it updates as you type.
                </p>
              </section>
            </aside>
          </>
        )}
      </div>
    </AppShell>
  )
}
